import { observable, runInAction } from "mobx"

import { createApiClient } from "@/lib/apiClient/client.ts"
import { LocalStoragePersistence } from "@/lib/persistence.ts"
import { QueriesImpl } from "@/lib/queries.ts"

import type { TokenSource } from "@/lib/client/WebSocketStableConnection.ts"
import type { AuthController } from "@/lib/oauth/AuthController.ts"
import type { Persistence } from "@/lib/persistence.ts"
import type { Queries } from "@/lib/queries.ts"
import type { QueryClient } from "@tanstack/react-query"
import type { BuildLocationFn } from "@tanstack/react-router"
import type { IObservableValue } from "mobx"

export interface GlobalContext {
  host: string
  queryClient: QueryClient
  authController: AuthController

  persistence: Persistence

  useLoader: boolean
  isLoading: IObservableValue<boolean>

  buildLocation: BuildLocationFn

  getOrgContext: () => OrgContext | null
  setOrgContext: (orgContext: OrgContext | null) => void
}

export interface OrgContext extends GlobalContext {
  orgId: string
  orgToken: TokenSource
  queries: Queries
}

export class GlobalContextImpl implements GlobalContext {
  public readonly host: string
  public readonly queryClient: QueryClient
  public readonly authController: AuthController

  public readonly persistence: Persistence

  public readonly useLoader: boolean
  public readonly isLoading: IObservableValue<boolean>

  public buildLocation: BuildLocationFn

  private orgContext: OrgContext | null
  public getOrgContext: () => OrgContext | null
  public setOrgContext: (orgContext: OrgContext | null) => void

  constructor(params: GlobalContextParams) {
    this.host = params.host
    this.queryClient = params.queryClient
    this.authController = params.authController

    this.persistence = new LocalStoragePersistence("app_")

    this.useLoader = true
    this.isLoading = observable.box(false)

    // Placeholder for buildLocation - will be patched after router creation
    this.buildLocation = (() => {
      throw new Error("buildLocation not initialized yet")
    }) as BuildLocationFn

    // org context cache
    this.orgContext = null
    this.getOrgContext = (() => {
      return this.orgContext
    })
    this.setOrgContext = ((orgContext: OrgContext | null) => {
      this.orgContext = orgContext
    })
  }
}

export async function createQueries(orgId: string, context: GlobalContext): Promise<Queries> {
  const apiClient = await createApiClient(
    context.host,
    orgId,
    context.authController.authenticatedUser(),
    context.authController.tokenSource
  )
  return new QueriesImpl(apiClient, context.queryClient)
}

type GlobalContextParams = {
  host: string
  queryClient: QueryClient
  authController: AuthController
}

export async function withGlobalLoader<T>(context: GlobalContext | null, promise: Promise<T>): Promise<T> {
  if (!context) return await promise

  runInAction(() => context.isLoading.set(true))

  try {
    return await promise
  } finally {
    runInAction(() => context.isLoading.set(false))
  }
}
